import { ScaleLinear, scaleLinear } from 'd3';
import {
  FullEngagementFragment,
  VoteForAdminConfig,
  VoteForAdminData,
  VoteForSubmissionData,
  VoteForViewConfig,
  VoteForViewData,
  useGetSubmissionsQuery,
  useVoteForDeclareWinnersMutation,
  useVoteForResetVotesMutation,
  useVoteForUndeclareWinnersMutation,
} from '@/generated/graphql';
import { useMemo } from 'react';
import {
  TypedEngagement,
  TypedSubmission,
  asTypedEngagement,
  asTypedSubmissionQueryResult,
} from '@/utils/engagementTypeUtils';
import { engagementDefinitions } from '@/engagements';
import { Button } from '@/components/ui/button';
import { computeVoteCounts } from '../utils';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

function VoteForAdminControlViewInner({
  engagement,
}: {
  engagement: TypedEngagement<
    VoteForAdminData,
    VoteForAdminConfig,
    VoteForViewData,
    VoteForViewConfig
  >;
}) {
  const { data: submissionsData } = useGetSubmissionsQuery({
    variables: { engagementId: engagement.id! },
    skip: !engagement.id,
  });

  const typedSubmissionsData =
    asTypedSubmissionQueryResult<VoteForSubmissionData>(
      submissionsData || { submissions: [] },
    );

  const lookup = useMemo(() => {
    if (!typedSubmissionsData.submissions) return null;
    return typedSubmissionsData.submissions.reduce(
      (acc, submission) => {
        acc[submission.id] = submission;
        return acc;
      },
      {} as Record<string, TypedSubmission<VoteForSubmissionData>>,
    );
  }, [typedSubmissionsData.submissions]);

  const [declareWinners] = useVoteForDeclareWinnersMutation();
  const [undeclareWinners] = useVoteForUndeclareWinnersMutation();
  const [resetVotes] = useVoteForResetVotesMutation();

  const engagementDef = engagementDefinitions[engagement.type];

  const voteCounts = useMemo(
    () => computeVoteCounts(engagement.viewData),
    [engagement.viewData],
  );

  const winners = engagement.data.voteWinners;

  const barWidthScale = useMemo(() => {
    const maxCount = Math.max(...Object.values(voteCounts), 10); // the fewest votes to be represented by 100% should be 10
    return scaleLinear().domain([0, maxCount]).range([3, 100]); // we want to start at 3% so that no votes has something
  }, [voteCounts]);

  return (
    <div
      data-name="VOTE_FOR_ADMIN_CONTROL_VIEW"
      className="flex h-full w-full items-start justify-start"
    >
      <div className="flex w-full flex-col gap-4 overflow-auto p-4">
        <h2 className="mb-3 flex flex-row items-center justify-center gap-2 text-2xl font-bold md:items-start md:justify-start">
          <engagementDef.Icon className="h-8 w-8" />
          <span>{engagement.title}</span>
        </h2>
        <div
          data-name="CHOICES_CTNR"
          className="border-bpy-8 my-4 flex flex-col justify-start gap-[5vh]"
        >
          {lookup
            ? engagement.data.choiceDisplayOrder.map((choiceId) => {
                const choice = lookup[choiceId];
                if (!choice) return null;
                return (
                  <Choice
                    key={choice.id}
                    choice={choice}
                    voteCount={voteCounts[choice.id]}
                    barWidthScale={barWidthScale}
                    isWinner={winners?.includes(choice.id)}
                    isLoser={Boolean(
                      winners?.length && !winners.includes(choice.id),
                    )}
                  />
                );
              })
            : null}
        </div>
        <div className="flex flex-row items-center justify-center gap-4">
          {!winners || winners.length === 0 ? (
            <Button
              variant="epic"
              onClick={() =>
                declareWinners({ variables: { engagementId: engagement.id! } })
              }
            >
              Declare Winner
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() =>
                undeclareWinners({
                  variables: { engagementId: engagement.id! },
                })
              }
            >
              Re-open Voting
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() =>
              resetVotes({ variables: { engagementId: engagement.id! } })
            }
          >
            Reset Votes
          </Button>
        </div>
        {/* <pre>{JSON.stringify(engagement.data, null, 2)}</pre> */}
      </div>
    </div>
  );
}

function Choice({
  choice,
  voteCount,
  barWidthScale,
  isWinner,
  isLoser,
}: {
  choice: TypedSubmission<VoteForSubmissionData>;
  voteCount: number;
  barWidthScale: ScaleLinear<number, number>;
  isWinner?: boolean;
  isLoser?: boolean;
}) {
  return (
    <div
      data-name="CHOICE"
      key={choice.id}
      className={cn(
        'relative flex flex-row items-center gap-3',
        'w-full',
        isLoser && 'z-20 opacity-50',
        isWinner && 'z-30',
      )}
    >
      <div className="flex flex-col items-center justify-center">
        <div
          className={`-rotate-32 absolute -left-1 -top-1 z-20 items-center gap-1 rounded-full text-lg text-epic-foreground ${isWinner ? 'scale-125 opacity-100' : 'scale-[300%] opacity-0'} transition-all`}
        >
          ⭐️
        </div>
        <img
          src={choice.data.optionalImageUrl || ''}
          alt={choice.data.title}
          className={`h-16 w-16 rounded-full border-4 transition-transform ${isWinner ? 'scale-125' : ''}`}
          style={{ borderColor: choice.data.color }}
        />
        <Badge
          className={`${isWinner ? '-bottom-4' : 'bottom-0'} absolute left-0 transition-all`}
          size={isWinner ? 'lg' : 'default'}
        >
          {voteCount || 0}
        </Badge>
      </div>
      <div className="flex-1">
        <h3 className="relative z-10 flex flex-row items-center justify-between gap-1">
          <span data-name="" className="font-bold">
            {choice.data.title}
          </span>
          {isWinner && <Badge variant="epic">winner!</Badge>}
        </h3>
        <div
          className="transition-width relative left-0 top-0 h-2 rounded transition-all"
          style={{
            width: `${barWidthScale(voteCount || 0)}%`,
            backgroundColor: choice.data.color,
          }}
        ></div>
      </div>
    </div>
  );
}

export function VoteForAdminControlView({
  engagement,
}: {
  engagement: FullEngagementFragment;
}) {
  const typedEngagement = asTypedEngagement<
    VoteForAdminData,
    VoteForAdminConfig,
    VoteForViewData,
    VoteForViewConfig
  >(engagement);
  return <VoteForAdminControlViewInner engagement={typedEngagement} />;
}
